import { useState, useEffect } from 'react';
import { LandingPage } from './components/LandingPage';
import { OnboardingFlow } from './components/OnboardingFlow';
import { Dashboard } from './components/Dashboard';
import { CreateGiftGroupFlow } from './components/CreateGiftGroupFlow';
import { GiftGroupAcceptance } from './components/GiftGroupAcceptance';
import { GiftGroupContribution } from './components/GiftGroupContribution';
import { AIGiftSuggestions } from './components/AIGiftSuggestions';
import { GiftSuggestions } from './components/GiftSuggestions';
import { GiftRanking } from './components/GiftRanking';
import { GiftGroupOverview } from './components/GiftGroupOverview';
import { DesignShowcase } from './components/DesignShowcase';
import { AuthFlow } from './components/AuthFlow';
import { AuthShowcase } from './components/AuthShowcase';
import { TermsAndConditions } from './components/TermsAndConditions';
import { FlowSubwayMap } from './components/FlowSubwayMap';
import { AdminDashboard } from './components/AdminDashboard';
import { Button } from './components/ui/button';
import { LogoIcon } from './components/Logo';
import { ChevronLeft, LogOut } from 'lucide-react';
import { api } from './lib/api';

type Screen = 
  | 'landing'
  | 'login'
  | 'verify'
  | 'register'
  | 'onboarding'
  | 'dashboard'
  | 'create-gift-group'
  | 'gift-group-acceptance'
  | 'gift-group-contribution'
  | 'ai-gift-suggestions'
  | 'gift-suggestions'
  | 'gift-ranking'
  | 'gift-group-overview'
  | 'design-showcase'
  | 'auth-showcase'
  | 'terms'
  | 'flow-map'
  | 'admin';

export default function App() {
  const [currentScreen, setCurrentScreen] = useState<Screen>('landing');
  const [magicToken, setMagicToken] = useState<string>('');
  const [magicContact, setMagicContact] = useState<string>('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [selectedGiftGroupId, setSelectedGiftGroupId] = useState<string | null>(null);
  const [selectedMemberId, setSelectedMemberId] = useState<string | null>(null);
  const [recommendedAmount, setRecommendedAmount] = useState<number | null>(null);
  const [dashboardTab, setDashboardTab] = useState<string>('todo');
  const [dashboardKey, setDashboardKey] = useState<number>(0); // Force dashboard refresh
  const [selectedView, setSelectedView] = useState<string | null>(null);

  // Check for magic link token in URL on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get('token');
    const contact = params.get('contact');
    const redirect = params.get('redirect');
    const pathname = window.location.pathname;

    // Handle invitation links: /invitation/:memberId
    if (pathname.startsWith('/invitation/')) {
      const memberId = pathname.split('/invitation/')[1];
      if (memberId) {
        setSelectedMemberId(memberId);
        setCurrentScreen('gift-group-acceptance');
        return;
      }
    }

    // Handle terms page: /terms
    if (pathname === '/terms') {
      setCurrentScreen('terms');
      return;
    }

    // Handle flow map page: /flow-map
    if (pathname === '/flow-map') {
      setCurrentScreen('flow-map');
      return;
    }

    // Handle admin page: /admin
    if (pathname === '/admin') {
      setCurrentScreen('admin');
      return;
    }

    if (token) {
      setMagicToken(token);
      if (contact) {
        setMagicContact(decodeURIComponent(contact));
      }
      
      // Save redirect path for after authentication
      if (redirect) {
        sessionStorage.setItem('auth_redirect', redirect);
        
        // If it's an invitation, extract and set memberId
        if (redirect.startsWith('/invitation/')) {
          const memberId = redirect.split('/invitation/')[1];
          if (memberId) {
            setSelectedMemberId(memberId);
          }
        }
      }
      
      setCurrentScreen('verify');
      // Clean up URL
      window.history.replaceState({}, '', window.location.pathname);
    }

    // Check if user is already authenticated
    setIsAuthenticated(api.isAuthenticated());
  }, []);

  // Update auth state when screen changes to dashboard
  useEffect(() => {
    if (currentScreen === 'dashboard') {
      setIsAuthenticated(api.isAuthenticated());
    }
  }, [currentScreen]);

  // Check for pending invitation when returning to gift-group-acceptance
  useEffect(() => {
    if (currentScreen === 'gift-group-acceptance') {
      const pendingMemberId = localStorage.getItem('pending_invitation_accept');
      if (pendingMemberId && !selectedMemberId) {
        setSelectedMemberId(pendingMemberId);
      }
    }
  }, [currentScreen, selectedMemberId]);

  const navigate = (screen: string, data?: { giftGroupId?: string; memberId?: string; tab?: string; view?: string; suggestionId?: string; refreshDashboard?: boolean; recommendedAmount?: number }) => {
    console.log('🚀 NAVIGATE called:');
    console.log('  Screen:', screen);
    console.log('  Data:', JSON.stringify(data, null, 2));
    
    // If navigating to dashboard, set the tab (default to 'todo' if not specified)
    if (screen === 'dashboard') {
      const newTab = data?.tab || 'todo';
      console.log('📍 Setting dashboardTab to:', newTab);
      setDashboardTab(newTab);
    }
    
    setCurrentScreen(screen as Screen);
    
    // Always set giftGroupId (null if not provided to clear previous value)
    setSelectedGiftGroupId(data?.giftGroupId ?? null);
    
    // Always set memberId (null if not provided to clear previous value)
    setSelectedMemberId(data?.memberId ?? null);
    
    // Always set recommendedAmount (null if not provided to clear previous value)
    setRecommendedAmount(data?.recommendedAmount ?? null);
    
    // Always set selectedView (null if not provided to clear previous value)
    setSelectedView(data?.view ?? null);
    
    // Force dashboard to re-mount and fetch fresh data
    if (screen === 'dashboard' || data?.refreshDashboard) {
      setDashboardKey(prev => prev + 1);
    }
  };

  const handleLogout = () => {
    api.logout();
    setIsAuthenticated(false);
    setCurrentScreen('landing');
  };

  const renderScreen = () => {
    switch (currentScreen) {
      case 'landing':
        return <LandingPage onNavigate={navigate} isAuthenticated={isAuthenticated} />;
      case 'login':
      case 'verify':
      case 'register':
        return (
          <AuthFlow 
            onNavigate={navigate} 
            initialScreen={currentScreen as 'login' | 'verify' | 'register'}
            magicToken={magicToken}
            magicContact={magicContact}
          />
        );
      case 'onboarding':
        return <OnboardingFlow onNavigate={navigate} />;
      case 'dashboard':
        return <Dashboard key={dashboardKey} onNavigate={navigate} initialTab={dashboardTab} giftGroupId={selectedGiftGroupId} recommendedAmount={recommendedAmount} />;
      case 'create-gift-group':
        return <CreateGiftGroupFlow onNavigate={navigate} />;
      case 'gift-group-acceptance':
        return <GiftGroupAcceptance onNavigate={navigate} memberId={selectedMemberId} />;
      case 'gift-group-contribution':
        return <GiftGroupContribution onNavigate={navigate} giftGroupId={selectedGiftGroupId} memberId={selectedMemberId} />;
      case 'ai-gift-suggestions':
        return <AIGiftSuggestions onNavigate={navigate} giftGroupId={selectedGiftGroupId} />;
      case 'gift-suggestions':
        return <GiftSuggestions onNavigate={navigate} giftGroupId={selectedGiftGroupId} />;
      case 'gift-ranking':
        return <GiftRanking onNavigate={navigate} giftGroupId={selectedGiftGroupId} />;
      case 'gift-group-overview':
        return <GiftGroupOverview onNavigate={navigate} giftGroupId={selectedGiftGroupId} initialView={selectedView as 'ranking' | 'ai-suggestions' | 'manual-suggestions' | undefined} />;
      case 'design-showcase':
        return <DesignShowcase />;
      case 'auth-showcase':
        return <AuthShowcase onNavigate={navigate} />;
      case 'terms':
        return <TermsAndConditions onBack={() => navigate('landing')} />;
      case 'flow-map':
        return <FlowSubwayMap onNavigate={navigate} />;
      case 'admin':
        return <AdminDashboard onNavigate={navigate} />;
      default:
        return <LandingPage onNavigate={navigate} isAuthenticated={isAuthenticated} />;
    }
  };

  // Don't show nav on auth screens or full-screen pages
  const authScreens: Screen[] = ['login', 'verify', 'register'];
  const fullScreenPages: Screen[] = ['flow-map', 'admin'];
  const showNav = currentScreen !== 'landing' && !authScreens.includes(currentScreen) && !fullScreenPages.includes(currentScreen);

  return (
    <div className="min-h-screen bg-background">
      {/* Simple top nav for demo navigation - remove in production */}
      {showNav && (
        <div className="bg-white border-b sticky top-0 z-50 shadow-sm">
          <div className="max-w-7xl mx-auto px-2 sm:px-4 py-2 flex items-center justify-between gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                console.log('🚀 App.tsx: Header Dashboard button clicked');
                console.log('🚀 Calling navigate with:', { screen: isAuthenticated ? 'dashboard' : 'landing', data: isAuthenticated ? { tab: 'todo' } : undefined });
                navigate(isAuthenticated ? 'dashboard' : 'landing', isAuthenticated ? { tab: 'todo' } : undefined);
              }}
              className="text-muted-foreground hover:text-primary flex-shrink-0"
            >
              <ChevronLeft className="w-4 h-4 mr-1" />
              <span className="hidden xs:inline">{isAuthenticated ? 'Dashboard' : 'Back to Home'}</span>
              <span className="xs:hidden">{isAuthenticated ? 'Dashboard' : 'Home'}</span>
            </Button>
            <LogoIcon size="sm" className="flex-shrink-0" />
            <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
              {isAuthenticated && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleLogout}
                  className="text-muted-foreground hover:text-red-600"
                >
                  <LogOut className="w-4 h-4 sm:mr-1" />
                  <span className="hidden sm:inline">Sign Out</span>
                </Button>
              )}
            </div>
          </div>
        </div>
      )}
      
      {renderScreen()}
    </div>
  );
}